import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { INK, MUTED_36, MUTED_45, MUTED_60, FONT as YFONT, ROOMS, glow, capsuleStyle, bubbleRight } from "@/lib/yuoDesign";
import RoomShell from "@/components/yuo/RoomShell";
import InputRow from "@/components/yuo/InputRow";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ConversationHistoryModal } from "@/components/ConversationHistoryModal";
import { UpgradeSheet } from "@/components/UpgradeSheet";
import { HighlightedText } from "@/components/HighlightedText";
import { useResponsive } from "@/lib/responsive";
import { MobileHeaderButton } from "@/components/mobile/MobileHeader";

const CORAL = ROOMS.content.hex;

/**
 * "Content" tab — a working conversation, not a one-shot card. Carries the
 * three method tools in the header (Script, Planning, Mirror-Diary) plus the
 * chat history, exactly as the Expo original did.
 */
export default function Content() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPhone } = useResponsive();

  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [voiceMeta, setVoiceMeta] = useState({ active: false });
  const [sending, setSending] = useState(false);
  const [weaving, setWeaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showMyNotes, setShowMyNotes] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(null);
  const [savedWord, setSavedWord] = useState(null);
  const listEndRef = useRef(null);

  const saveWordToDiary = async (word, kind) => {
    try {
      await api.createDiary({ title: `Creator: ${word}`, content: `[${kind}] ${word}` });
      setSavedWord(word);
      setTimeout(() => setSavedWord(null), 1800);
    } catch (e) {
      console.warn("save word failed", e);
    }
  };

  const loadConversations = useCallback(async () => {
    const convs = await api.listConversations("content");
    setConversations(convs || []);
    return convs || [];
  }, []);

  const loadMessages = useCallback(async (cid) => {
    const data = await api.getConversation(cid);
    setMessages(data.messages || []);
  }, []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const convs = await loadConversations();
      if (convs.length > 0) {
        setConversationId(convs[0].conversation_id);
        await loadMessages(convs[0].conversation_id);
      } else {
        setConversationId(null);
        setMessages([]);
      }
    } catch (e) {
      console.warn("content load failed", e);
    } finally {
      setLoading(false);
    }
  }, [loadConversations, loadMessages]);

  useEffect(() => {
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const ensureConversation = async () => {
    if (conversationId) return conversationId;
    try {
      const conv = await api.createConversation("content");
      setConversationId(conv.conversation_id);
      await loadConversations();
      return conv.conversation_id;
    } catch (e) {
      if (e?.status === 402) setShowUpgrade("A second Creator chat requires YUO Pro.");
      throw e;
    }
  };

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    const optimistic = { message_id: `tmp_${Date.now()}`, role: "user", content };
    try {
      const cid = await ensureConversation();
      setMessages((p) => [...p, optimistic]);
      const voiceFlag = voiceMeta.active;
      const voiceLang = voiceMeta.language || null;
      setText("");
      setVoiceMeta({ active: false });
      const res = await api.sendMessage(cid, content, undefined, {
        via_voice: voiceFlag,
        voice_language: voiceLang,
      });
      setMessages((p) => {
        const seen = new Set([res.user_message.message_id, res.assistant_message.message_id]);
        const w = p.filter((m) => m.message_id !== optimistic.message_id && !seen.has(m.message_id));
        return [...w, res.user_message, res.assistant_message];
      });
      await loadConversations();
    } catch (e) {
      if (e?.status === 402) {
        setMessages((p) => p.filter((m) => m.message_id !== optimistic.message_id));
      } else {
        const errText =
          e?.status === 0
            ? "Could not reach YUO. Check your connection and try again."
            : e?.message || "No reply arrived. Try again.";
        setMessages((p) => {
          const withoutTmp = p.filter((m) => m.message_id !== optimistic.message_id);
          return [
            ...withoutTmp,
            optimistic,
            { message_id: `err_${Date.now()}`, role: "assistant", content: `⚠ ${errText}` },
          ];
        });
      }
    } finally {
      setSending(false);
    }
  };

  const weaveMirrorDiary = async () => {
    if (weaving) return;
    setWeaving(true);
    try {
      const res = await api.contentMirrorDiary();
      // The endpoint appends the reflection to the user's Content conversation,
      // so reload the thread rather than holding it in local state only.
      const convs = await loadConversations();
      if (convs.length > 0) {
        setConversationId(convs[0].conversation_id);
        await loadMessages(convs[0].conversation_id);
      } else if (res?.assistant_message) {
        setMessages((p) => [...p, res.assistant_message]);
      }
    } catch (e) {
      if (e?.status === 402) {
        setShowUpgrade("A second Creator chat requires YUO Pro.");
      } else {
        setMessages((p) => [
          ...p,
          {
            message_id: `err_${Date.now()}`,
            role: "assistant",
            content: `⚠ ${e?.message || "Could not weave the reflection. Try again."}`,
          },
        ]);
      }
    } finally {
      setWeaving(false);
    }
  };

  const startNewChat = async () => {
    try {
      const conv = await api.createConversation("content");
      setConversationId(conv.conversation_id);
      setMessages([]);
      await loadConversations();
    } catch (e) {
      if (e?.status === 402) setShowUpgrade("A second Creator chat requires YUO Pro.");
    }
  };

  const openChat = async (cid) => {
    setConversationId(cid);
    setMessages([]);
    setLoading(true);
    try {
      await loadMessages(cid);
    } finally {
      setLoading(false);
    }
  };

  const removeChat = async (cid) => {
    await api.deleteConversation(cid);
    const convs = await loadConversations();
    if (cid === conversationId) {
      if (convs.length > 0) {
        setConversationId(convs[0].conversation_id);
        await loadMessages(convs[0].conversation_id);
      } else {
        setConversationId(null);
        setMessages([]);
      }
    }
  };

  const showLegend = messages.some(
    (m) => m.role === "assistant" && /\[\[(coral|purple|green):/.test(m.content || ""),
  );

  const toolActions = (
    <>
      <button data-testid="content-script-btn" onClick={() => navigate("/screenwriter")}
        style={{ ...capsuleStyle({ active: false, rgb: ROOMS.mirror.rgb }), gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%",
          background: `radial-gradient(38% 34% at 34% 26%, #fff, ${ROOMS.mirror.hex} 30%, #05060c 100%)`,
          boxShadow: `0 0 8px ${ROOMS.mirror.hex}` }} />
        Script
      </button>
      <button data-testid="content-planning-btn" onClick={() => navigate("/planning")}
        style={{ ...capsuleStyle({ active: false, rgb: ROOMS.symbiosis.rgb }), gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%",
          background: `radial-gradient(38% 34% at 34% 26%, #fff, ${ROOMS.symbiosis.hex} 30%, #05060c 100%)`,
          boxShadow: `0 0 8px ${ROOMS.symbiosis.hex}` }} />
        Planning
      </button>
      <button data-testid="content-record-btn"
        onClick={() => { if (user?.plan === "master") { window.location.href = "/creator-record.html"; } else { navigate("/paywall?plan=master&feature=studio"); } }}
        style={{ ...capsuleStyle({ active: false, rgb: ROOMS.content.rgb }), gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%",
          background: `radial-gradient(38% 34% at 34% 26%, #fff, ${ROOMS.content.hex} 30%, #05060c 100%)`,
          boxShadow: `0 0 10px ${ROOMS.content.hex}` }} />
        Studio
      </button>
      <button data-testid="content-mirror-diary-btn" onClick={weaveMirrorDiary} disabled={weaving}
        style={{ ...capsuleStyle({ active: false, rgb: ROOMS.diary.rgb }), gap: 8, opacity: weaving ? 0.6 : 1 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%",
          background: `radial-gradient(38% 34% at 34% 26%, #fff, ${ROOMS.diary.hex} 30%, #05060c 100%)`,
          boxShadow: `0 0 8px ${ROOMS.diary.hex}` }} />
        {weaving ? "Weaving…" : "Mirror-Diary"}
      </button>
      <button data-testid="content-history-button" onClick={() => setShowHistory(true)}
        style={capsuleStyle({ active: false })}>History · {conversations.length}</button>
      <button data-testid="content-new-chat" onClick={startNewChat}
        style={capsuleStyle({ active: true, room: "content" })}>+ New chat</button>
    </>
  );

  const openStudio = () => { if (user?.plan === "master") { window.location.href = "/creator-record.html"; } else { navigate("/paywall?plan=master&feature=studio"); } };

  const phoneRows = [
    { label: `History · ${conversations.length}`, hint: "previous chats", color: "content", onClick: () => setShowHistory(true), testId: "m-content-history" },
    { label: "Script", hint: "screenwriter", color: "mirror", onClick: () => navigate("/screenwriter"), testId: "m-content-script" },
    { label: "Planning", hint: "five colours · budget", color: "symbiosis", onClick: () => navigate("/planning"), testId: "m-content-planning" },
    { label: "Studio", hint: user?.plan === "master" ? "record" : "master plan", color: "content", onClick: openStudio, testId: "m-content-studio" },
    { label: weaving ? "Weaving…" : "Mirror-Diary", hint: "weave chats + diary", color: "diary", onClick: weaveMirrorDiary, disabled: weaving, testId: "m-content-mirror-diary" },
  ];

  return (
    <RoomShell
      room="content"
      testId="content-screen"
      title="Creator"
      subtitle="expression"
      actions={toolActions}
      mobileActions={
        <MobileHeaderButton room="content" solid onClick={startNewChat} testId="content-new-chat-m">+ New chat</MobileHeaderButton>
      }
      moreRows={phoneRows}
      moreTitle="Creator · tools"
      footer={
        <InputRow
          room="content"
          value={text}
          onChange={(v) => { setText(v); if (!v.trim()) setVoiceMeta({ active: false }); }}
          onSend={send}
          placeholder="speak…"
          inputTestId="content-input"
          sendTestId="content-send-button"
          onAskYuo={() => setText((p) => p.trim() ? `${p}\n\nYUO, ?` : "YUO, ?")}
        />
      }
    >
      {/* Message stream — tools no longer inside content, they live in header */}
      <div data-testid="content-messages-list"
        style={{ maxWidth: 1060, margin: "0 auto", display: "flex", flexDirection: "column", gap: isPhone ? 14 : 22 }}>
        {loading ? (
          <div style={{ color: MUTED_36, fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", padding: 30, textAlign: "center" }}>
            listening…
          </div>
        ) : (
          <>
            {messages.length === 0 && !weaving && (
              <div data-testid="content-empty"
                style={{ color: MUTED_60, fontSize: 13, lineHeight: 1.65, maxWidth: 620, margin: "20px auto", textAlign: "center" }}>
                {isPhone
                  ? "Describe an idea, a feeling or a topic — or open ⋯ and tap Mirror-Diary to weave your recent chats and diary entries into a fresh piece of insight."
                  : "Describe an idea, a feeling or a topic — or tap Mirror-Diary in the header to weave your recent chats and diary entries into a fresh piece of insight."}
              </div>
            )}
            {messages.map((m) => <Bubble key={m.message_id} msg={m} onWordPress={saveWordToDiary} phone={isPhone} />)}
            {weaving && (
              <div data-testid="content-weaving" style={{ color: MUTED_45, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", padding: 12 }}>
                Weaving Mirror × Diary…
              </div>
            )}
            <div ref={listEndRef} />
          </>
        )}
      </div>

      <ConversationHistoryModal
        visible={showHistory} onClose={() => setShowHistory(false)}
        items={conversations} activeId={conversationId} accent={ROOMS.content.hex}
        onSelect={openChat} onNew={startNewChat} onDelete={removeChat}
      />

      <UpgradeSheet visible={!!showUpgrade} reason={showUpgrade || ""} isPro={user?.plan === "pro"} onClose={() => setShowUpgrade(null)} />

      {savedWord && (
        <div data-testid="word-saved-toast" style={{
          position: "fixed", bottom: 110, left: "50%", transform: "translateX(-50%)",
          background: `rgba(${ROOMS.diary.rgb},0.12)`, boxShadow: `0 0 22px rgba(${ROOMS.diary.rgb},0.28)`,
          padding: "10px 18px", borderRadius: 999, color: INK, fontFamily: YFONT, fontSize: 12, letterSpacing: "0.14em",
        }}>
          saved "{savedWord}" to Diary
        </div>
      )}
    </RoomShell>
  );
}

// removed ToolTile — tools now live in the header actions as capsule pills

function Bubble({ msg, onWordPress, phone = false }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div style={{
          maxWidth: phone ? "84%" : "72%", padding: phone ? "14px 18px" : "18px 22px", borderRadius: 26,
          background: bubbleRight(ROOMS.mirror.rgb, 0.22),
          fontSize: 13.5, lineHeight: 1.65, color: INK, whiteSpace: "pre-wrap",
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
        }}>{msg.content}</div>
      </div>
    );
  }
  return (
    <div style={{
      padding: phone ? "16px 18px" : "22px 24px", borderRadius: 28,
      background: glow(ROOMS.content.rgb, 0.16),
      fontSize: 13.5, lineHeight: 1.7, color: "var(--role-coral-ink)",
      animation: "yuo-emerge 1.1s cubic-bezier(.2,.7,.2,1) both",
    }}>
      <HighlightedText content={msg.content} onWordPress={onWordPress}
        baseStyle={{ color: "var(--role-coral-ink)", fontFamily: YFONT, fontSize: 13.5, lineHeight: 1.7 }} />
    </div>
  );
}
